import {
    fetchMyInfo,
    logout,
    patchMyInfo,
    verifyLoginWithSMSCode,
} from '@/api/user';
import {MyInfoT} from '@/types/user-types';
import {
    clearExcelDownloadVerifiedCookieFlag,
    clearNewFeatureTooltipDismissed,
    clearTokens,
    getAccessToken,
    getExcelDownloadVerifiedCookieFlag,
    getRefreshToken,
    setExcelDownloadVerifiedCookieFlag,
    setTokens,
} from '@/utils/storage';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {defineStore} from 'pinia';
// import useMemberListStore from './member-list-store';
// import useStatisticsStore from './statistics-store';

// const memberListStore = useMemberListStore();
// const statisticsStore = useStatisticsStore();

type MyInfoType<T> = T extends true ? MyInfoT : null;

const useUserStore = defineStore('user', {
    state: () => ({
        myInfo: null as MyInfoType<boolean>,
        isExcelDownloadVerified: getExcelDownloadVerifiedCookieFlag(),
    }),
    getters: {
        getMyInfo: (state) => {
            return state.myInfo as MyInfoType<true>;
        },
        isLogin: (state) => {
            return state.myInfo !== null;
        },
    },
    actions: {
        async checkAuth() {
            try {
                const accessToken = getAccessToken();

                if (accessToken) {
                    this.myInfo = await fetchMyInfo();

                    Sentry.setUser({
                        companyId: this.myInfo.companyId,
                        staffId: this.myInfo.staffId,
                    });
                }
            } catch (err) {
                const axiosError = err as AxiosError;

                // NOTE: 다른 PC 등 다른 환경에서 추가로 로그인 한 경우, 만료 경우
                if (axiosError.status == '401') {
                    clearTokens();
                    this.myInfo = null;
                    clearExcelDownloadVerifiedCookieFlag();
                    Sentry.setUser(null);
                }

                Sentry.captureException(axiosError, {
                    level: 'error',
                    extra: {
                        code: axiosError.code,
                        status: axiosError.response?.status,
                        request: axiosError.request,
                        response: axiosError.response?.data,
                        message: axiosError.message,
                        from: 'src/store/user-store.ts',
                    },
                });
                window.customAlert(axiosError.message);
            }
        },
        async login(phoneNumber: string, loginToken: string, smsCode: string) {
            const tokens = await verifyLoginWithSMSCode(
                phoneNumber,
                loginToken,
                smsCode,
            );

            setTokens(tokens);

            // HACK: 엑셀다운로드 쿠키 초기화
            clearExcelDownloadVerifiedCookieFlag();
        },
        async logout() {
            try {
                const accessToken = getAccessToken();
                const refreshToken = getRefreshToken();

                await logout({accessToken, refreshToken});
            } catch (err) {
                const axiosError = err as AxiosError;

                Sentry.captureException(axiosError, {
                    level: 'error',
                    extra: {
                        code: axiosError.code,
                        status: axiosError.response?.status,
                        header: axiosError.config.headers,
                        request: axiosError.request,
                        response: axiosError.response?.data,
                        message: axiosError.message,
                        from: 'src/store/user-store.ts',
                    },
                });

                console.error(axiosError.message);
            } finally {
                // HACK: api 성공 여부와 상관없이 토큰, 엑셀다운로드 쿠키 삭제
                clearTokens();
                clearExcelDownloadVerifiedCookieFlag();
                clearNewFeatureTooltipDismissed();

                this.myInfo = null;
                Sentry.setUser(null);
                // memberListStore.$reset();
                // statisticsStore.$reset();
            }
        },
        async modifyMyInfo(name: string) {
            this.myInfo = await patchMyInfo({name});

            Sentry.setUser({
                companyId: this.myInfo.companyId,
                staffId: this.myInfo.staffId,
            });
        },
        updateExcelDownloadVerified(isVerified: 'true' | 'false') {
            setExcelDownloadVerifiedCookieFlag(isVerified);

            this.isExcelDownloadVerified = getExcelDownloadVerifiedCookieFlag();
        },
    },
});

export default useUserStore;
